import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
});

export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  verify2fa: (challengeId, code) => api.post('/auth/verify-2fa', { challenge_id: challengeId, code }),
  resend2fa: (challengeId) => api.post('/auth/resend-2fa', { challenge_id: challengeId }),
};

export const productsAPI = {
  getAll: (userId) => api.get('/products', { params: { user_id: userId } }),
  getOne: (id) => api.get(`/products/${id}`),
  create: (data, userId) => api.post('/products', data, { params: { user_id: userId } }),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
};

export const formulasAPI = {
  getByProduct: (productId) => api.get(`/formulas/product/${productId}`),
  create: (data) => api.post('/formulas', data),
  update: (id, data) => api.put(`/formulas/${id}`, data),
};

export const suppliersAPI = {
  getAll: (userId) => api.get('/suppliers', { params: { user_id: userId } }),
  create: (data, userId) => api.post('/suppliers', data, { params: { user_id: userId } }),
};

export const certificatesAPI = {
  getByProduct: (productId) => api.get(`/certificates/product/${productId}`),
  upload: (formData) => api.post('/certificates/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  download: (certificateId) => api.get(`/certificates/download/${certificateId}`, {
    responseType: 'blob',
  }),
  delete: (certificateId) => api.delete(`/certificates/${certificateId}`),
};

export const manufacturingAPI = {
  getByProduct: (productId) => api.get(`/manufacturing/product/${productId}`),
  create: (data) => api.post('/manufacturing', data),
  update: (id, data) => api.put(`/manufacturing/${id}`, data),
  aiReview: (id) => api.post(`/manufacturing/${id}/ai-review`),
};

export const toxicologyAPI = {
  getByProduct: (productId) => api.get(`/toxicology/product/${productId}`),
  upload: (formData) => api.post('/toxicology/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  download: (toxicologyId) => api.get(`/toxicology/download/${toxicologyId}`, {
    responseType: 'blob',
  }),
};

export const aiAPI = {
  assistant: (data) => api.post('/ai/assistant', data),
  suggestIngredients: (data) => api.post('/ai/suggest-ingredients', data),
  translateToInci: (frenchName) => api.post('/ai/translate-to-inci', { query: frenchName, context: 'INCI' }),
  findCas: (name) => api.post('/ai/find-cas', { query: name, context: 'CAS' }),
  productionAssistant: (message, userId) => api.post('/ai/production-assistant', { message }, { params: { user_id: userId } }),
  translateInci: (ingredients) => api.post('/ai/translate-inci', { ingredients }),
  rewriteBpf: (data) => api.post('/ai/rewrite-bpf', data),
};

export const dipAPI = {
  generate: (productId) => api.post('/dip/generate', { product_id: productId }, { responseType: 'blob' }),
  getStatus: (productId) => api.get(`/dip/status/${productId}`),
};

export const labSettingsAPI = {
  get: (userId) => api.get('/lab-settings', { params: { user_id: userId } }),
  update: (data, userId) => api.put('/lab-settings', data, { params: { user_id: userId } }),
};

export const productInfoAPI = {
  update: (productId, data) => api.put(`/products/${productId}/info`, data),
  uploadLabel: (productId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/products/${productId}/label/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  lockRecipe: (productId) => api.post(`/products/${productId}/lock-recipe`),
  unlockRecipe: (productId) => api.post(`/products/${productId}/unlock-recipe`),
  acknowledgeModification: (productId) => api.post(`/products/${productId}/acknowledge-modification`),
};

// ==================== Production Management APIs ====================

export const materialsAPI = {
  getAll: (userId) => api.get('/materials', { params: { user_id: userId } }),
  getOne: (id) => api.get(`/materials/${id}`),
  create: (data, userId) => api.post('/materials', data, { params: { user_id: userId } }),
  update: (id, data, userId) => api.put(`/materials/${id}`, data, { params: { user_id: userId } }),
  delete: (id) => api.delete(`/materials/${id}`),
  getLowStockAlerts: (userId) => api.get('/materials/alerts/low-stock', { params: { user_id: userId } }),
};

export const finishedProductsAPI = {
  getAll: (userId) => api.get('/finished-products', { params: { user_id: userId } }),
  getOne: (id) => api.get(`/finished-products/${id}`),
  create: (data, userId) => api.post('/finished-products', data, { params: { user_id: userId } }),
  update: (id, data) => api.put(`/finished-products/${id}`, data),
  delete: (id) => api.delete(`/finished-products/${id}`),
  uploadImage: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/finished-products/${id}/upload-image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getImage: (id) => api.get(`/finished-products/${id}/image`, { responseType: 'blob' }),
};

export const recipeCompositionsAPI = {
  getByProduct: (productId) => api.get(`/recipe-compositions/product/${productId}`),
  getForJournal: (fpId) => api.get(`/finished-products/${fpId}/recipe-compositions`),
  create: (data) => api.post('/recipe-compositions', data),
  update: (id, data) => api.put(`/recipe-compositions/${id}`, data),
  delete: (id) => api.delete(`/recipe-compositions/${id}`),
};

export const fdsAPI = {
  upload: (materialId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/materials/${materialId}/fds/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  download: (fdsId) => api.get(`/fds/download/${fdsId}`, { responseType: 'blob' }),
  delete: (fdsId) => api.delete(`/fds/${fdsId}`),
};

export const recipeIngredientsAPI = {
  getByDipProduct: (productId) => api.get(`/products/${productId}/recipe-ingredients`),
};

export const productionJournalAPI = {
  getAll: (userId, limit = 100) => api.get('/production-journal', { params: { user_id: userId, limit } }),
  create: (data, userId) => api.post('/production-journal', data, { params: { user_id: userId } }),
  delete: (id) => api.delete(`/production-journal/${id}`),
};

export const productionDashboardAPI = {
  getStats: (userId) => api.get('/production-dashboard', { params: { user_id: userId } }),
  getPriceHistory: (userId, limit = 50) => api.get('/price-history', { params: { user_id: userId, limit } }),
};

// ==================== Planning & Forecasting APIs ====================

export const planningAPI = {
  getSimulations: (userId) => api.get('/production-simulations', { params: { user_id: userId } }),
  createSimulation: (data, userId) => api.post('/production-simulations', data, { params: { user_id: userId } }),
  deleteSimulation: (id) => api.delete(`/production-simulations/${id}`),
  getShoppingList: (userId) => api.get('/shopping-list', { params: { user_id: userId } }),
  getShoppingListBySupplier: (userId) => api.get('/shopping-list/by-supplier', { params: { user_id: userId } }),
};

export default api;
